import { useEffect, useRef, useState } from 'react'
import BlurHashImage from '@components/BlurHashImage'
import { BLUR_HASH_MAP } from '@constants/BlurHashItems'
import { imageLoadCache } from '@utils/image-load-cache'
import images from '@assets/images'
import './LoadingScreen.css'

const FILL_DURATION = 3000   // ms to ease 0 → 95%
const FINISH_DURATION = 750  // ms for linear 95-ish → 100% once load completes

function easeOutCubic(t) {
  return 1 - Math.pow(1 - t, 3)
}

function easeInOutCubic(t) {
  return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2
}

function LoadingScreen({ onFinished }) {
  const [fade, setFade] = useState(false)
  const [entered, setEntered] = useState(false)
  const [bgLoaded, setBgLoaded] = useState(() => imageLoadCache.has(images.loadingImg))

  const loadDoneRef = useRef(false)
  const progressRef = useRef(0)       // float position, updated every rAF
  const startTimeRef = useRef(null)
  const finishStartTimeRef = useRef(null)
  const finishStartProgressRef = useRef(null)
  const fillRef = useRef(null)        // DOM ref — bypasses React state for smooth width
  const rafRef = useRef(null)
  const fadeTimerRef = useRef(null)
  const onFinishedTimerRef = useRef(null)
  const finishingRef = useRef(false)

  const setWidth = (pct) => {
    if (fillRef.current) fillRef.current.style.width = pct.toFixed(2) + '%'
  }

  useEffect(() => {
    const entryTimer = setTimeout(() => setEntered(true), 80)

    const img = new Image()
    img.src = images.loadingImg
    const handleLoadComplete = () => {
      loadDoneRef.current = true
      imageLoadCache.add(images.loadingImg)
      setBgLoaded(true)
    }
    if (img.complete) {
      handleLoadComplete()
    } else {
      img.onload = handleLoadComplete
      img.onerror = handleLoadComplete
    }

    const animate = (now) => {
      if (finishingRef.current) return
      if (!startTimeRef.current) startTimeRef.current = now

      const elapsed = now - startTimeRef.current

      if (loadDoneRef.current) {
        // Start the finish run the first time we see loadDone
        if (finishStartTimeRef.current === null) {
          finishStartTimeRef.current = now
          finishStartProgressRef.current = progressRef.current
        }

        const ft = Math.min((now - finishStartTimeRef.current) / FINISH_DURATION, 1)
        const from = finishStartProgressRef.current
        progressRef.current = from + (100 - from) * easeInOutCubic(ft)
        setWidth(progressRef.current)

        if (ft >= 1) {
          finishingRef.current = true
          setWidth(100)
          fadeTimerRef.current = setTimeout(() => {
            setFade(true)
            onFinishedTimerRef.current = setTimeout(() => { if (onFinished) onFinished() }, 900)
          }, 400)
          return
        }
      } else {
        // Phase 1: ease to 95% in FILL_DURATION
        const t = Math.min(elapsed / FILL_DURATION, 1)
        let target = easeOutCubic(t) * 95
        if (t >= 1) {
          // Slow crawl 95 → 98 while waiting for load
          target = Math.min(95 + (elapsed - FILL_DURATION) * 0.0008, 98)
        }
        // Lerp toward target for smooth deceleration
        progressRef.current += (target - progressRef.current) * 0.035
        setWidth(progressRef.current)
      }

      rafRef.current = requestAnimationFrame(animate)
    }

    rafRef.current = requestAnimationFrame(animate)

    return () => {
      img.onload = null
      img.onerror = null
      if (rafRef.current) cancelAnimationFrame(rafRef.current)
      clearTimeout(entryTimer)
      clearTimeout(fadeTimerRef.current)
      clearTimeout(onFinishedTimerRef.current)
    }
  }, [onFinished])

  return (
    <div className={`loading-screen${fade ? ' loading-screen--fade-out' : ''}`}>
      {/* Background — static */}
      <div
        className="loading-screen__bg"
        style={{ backgroundImage: `url(${images.loadingImg})` }}
      />
      {/* BlurHash placeholder below overlay — fades out when bg image is ready */}
      <div className="loading-screen__blur-layer">
        <BlurHashImage hash={BLUR_HASH_MAP.loading} fadeOut={bgLoaded} />
      </div>
      <div className="loading-screen__overlay" />

      {/* Mobile: logo centered */}
      <img
        src={images.logo}
        alt="Fenica"
        className={`loading-screen__logo-mobile${entered ? ' is-visible' : ''}`}
      />

      {/* Bottom row: logo (desktop only) + progress bar */}
      <div className={`loading-screen__bottom${entered ? ' is-visible' : ''}`}>
        <img
          src={images.logo}
          alt="Fenica"
          className="loading-screen__logo-desktop"
        />
        <div className="loading-screen__progress-track">
          <div
            ref={fillRef}
            className="loading-screen__progress-fill"
            style={{ width: '0%' }}
          />
        </div>
      </div>
    </div>
  )
}

export default LoadingScreen
